import {
  ChevronsUpDown,
  CogIcon,
  FileCheckIcon,
  FlaskConicalIcon,
  HelpCircleIcon,
  KanbanSquareIcon,
  LayersIcon,
  LayoutDashboardIcon,
  ListIcon,
  LogOutIcon,
  MessageCircleIcon,
  SearchIcon,
  SquarePenIcon,
  TableIcon,
  UserIcon,
} from "lucide-react";
import { MenuTrigger } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { Avatar, getAvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import {
  Menu,
  MenuHeader,
  MenuItem,
  MenuSeparator,
} from "@/components/ui/menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuLink,
} from "@/components/ui/sidebar";
import { getRandomColor } from "@/components/ui/utils";
import { useFeatureFlags } from "@/features/admin";
import { logout } from "@/features/auth/api";
import { useAuth } from "@/features/auth/hooks";
import { usePendingReports } from "@/features/reports/hooks/usePendingReports";
import { useConfig } from "@/lib/config";
import { useProductlaneChat } from "@/lib/productlane";
import { usePermissions } from "@/lib/usePermissions";
import { PermissionGate } from "../PermissionGate";
import { RoleCondition } from "../RoleCondition";

export function AppSidebar() {
  const { featureFlags } = useFeatureFlags();
  const { user } = useAuth();
  const config = useConfig();

  const { data: views } = trpc.accession.views.list.useQuery();

  const { can } = usePermissions();
  const { accessions } = usePendingReports(
    can("read:reports") && can("read:test_runs"), // use read:test_runs to make sure this is only enabled for lab roles
  );
  const pending = accessions.length.toString();

  return (
    <Sidebar>
      <SidebarHeader>
        <SidebarMenu className="gap-2">
          <SidebarMenuItem>
            <SidebarMenuLink
              to="/"
              size="lg"
              activeProps={{ className: "" }}
              className="outline-none focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-ring focus-visible:rounded-md"
            >
              <div className="flex flex-col">
                <img
                  src={config.logoUrl}
                  className="object-contain max-w-full max-h-8 text-left dark:brightness-200 dark:contrast-50"
                  alt="logo"
                />
              </div>
            </SidebarMenuLink>
          </SidebarMenuItem>
          {config.testEnv && !featureFlags?.hideTestEnvironmentBadge && (
            <SidebarMenuItem className="px-2">
              <Badge color="red" className=" w-full">
                Test environment
              </Badge>
            </SidebarMenuItem>
          )}
          <RoleCondition
            roles={[
              "blis_org_tech",
              "blis_lab_assistant",
              "blis_lab_tech",
              "blis_lab_manager",
            ]}
          >
            <SidebarMenuItem className="px-2">
              <SidebarMenuLink
                to="/accessions/new"
                activeProps={{}}
                className={buttonVariants({
                  variant: "outline",
                  className: "bg-background",
                })}
              >
                <SquarePenIcon />
                New accession
              </SidebarMenuLink>
            </SidebarMenuItem>
          </RoleCondition>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuLink to="/">
                  <LayoutDashboardIcon />
                  Dashboard
                </SidebarMenuLink>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuLink to="/search">
                  <SearchIcon />
                  Search
                </SidebarMenuLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Accessions</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuLink
                  to="/accessions"
                  activeOptions={{ exact: true }}
                >
                  <LayersIcon />
                  Accessions
                </SidebarMenuLink>
              </SidebarMenuItem>
              {views?.map((view) => (
                <SidebarMenuItem key={view.id} title={view.name}>
                  <SidebarMenuLink to="/views/$id" params={{ id: view.id }}>
                    {view.display === "BOARD" && <KanbanSquareIcon />}
                    {view.display === "LIST" && <ListIcon />}
                    {view.display === "TABLE" && <TableIcon />}
                    <span>{view.name}</span>
                  </SidebarMenuLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <PermissionGate
          requiredAnyPermissions={[
            "admin:all",
            "read:reports",
            "read:test_runs",
          ]}
          labRole
        >
          <SidebarGroup>
            <SidebarGroupLabel>Lab</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuLink to="/runs">
                    <FlaskConicalIcon />
                    Test runs
                  </SidebarMenuLink>
                </SidebarMenuItem>
                <PermissionGate requiredPermission="read:reports">
                  <SidebarMenuItem>
                    <SidebarMenuLink to="/reports">
                      <FileCheckIcon />
                      Reports
                      {pending !== "0" && (
                        <SidebarMenuBadge>{pending}</SidebarMenuBadge>
                      )}
                    </SidebarMenuLink>
                  </SidebarMenuItem>
                </PermissionGate>
                <PermissionGate requiredPermission="admin:all">
                  <SidebarMenuItem>
                    <SidebarMenuLink to="/admin/tests">
                      <CogIcon />
                      Admin
                    </SidebarMenuLink>
                  </SidebarMenuItem>
                </PermissionGate>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </PermissionGate>
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuLink size="sm" to="/help">
                  <HelpCircleIcon />
                  Help and Support
                </SidebarMenuLink>
              </SidebarMenuItem>
              <ChatButton />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <UserMenu />
      </SidebarFooter>
    </Sidebar>
  );
}

function ChatButton() {
  const { buttonProps } = useProductlaneChat();

  return (
    <SidebarMenuItem>
      <SidebarMenuButton size="sm" {...buttonProps}>
        <MessageCircleIcon />
        Send feedback
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function UserMenu() {
  const { user } = useAuth();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <MenuTrigger>
          <SidebarMenuButton
            size="lg"
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <Avatar
              className="size-8"
              initials={getAvatarFallback(user?.profile.username)}
              color={getRandomColor(user?.profile.id)}
            />
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">
                {user.profile.username}
              </span>
              <span className="truncate text-xs font-normal">
                {user.profile.org.name}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto size-4" />
          </SidebarMenuButton>
          <Menu
            className="max-w-56"
            // placement="right"
            // side={isMobile ? "bottom" : "right"}
            // align="end"
            // sideOffset={4}
          >
            <MenuHeader>
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar
                  className="size-8"
                  initials={getAvatarFallback(user?.profile.username)}
                  color={getRandomColor(user?.profile.id)}
                />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {user.profile.username}
                  </span>
                  <span className="truncate text-xs font-normal">
                    {user.profile.org.name}
                  </span>
                </div>
              </div>
            </MenuHeader>
            <MenuItem href={{ to: "/auth/profile" }}>
              <UserIcon />
              View profile
            </MenuItem>
            <MenuSeparator />
            <MenuItem onAction={() => logout()}>
              <LogOutIcon />
              Sign out
            </MenuItem>
          </Menu>
        </MenuTrigger>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
